import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PasswordInput, Popover, InputField, Button } from '@ets/design-system';

import { navigateToMyFiles } from 'router/navigation';
import { useDispatch, useEffectOnUpdate, useSelector } from '_common/hooks';
import { PasswordCheck } from '_common/components';
import { signedOut } from 'Auth/redux/authSlice';
import SecondaryAuthPageContainer from 'Auth/components/SecondaryAuthPageContainer/SecondaryAuthPageContainer';
import { useGetCurrentUserQuery, useUpdatePasswordMutation } from '_common/services/api/authority';

const UpdatePasswordPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isUpdatePasswordForced = useSelector((state) => state.auth.isUpdatePasswordForced);
  const { data: currentUser } = useGetCurrentUserQuery();
  const [updatePassword] = useUpdatePasswordMutation();

  const [newPassword, setNewPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [validations, setValidations] = useState({
    newPassword: '',
    confirmedPassword: '',
  });
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (!isUpdatePasswordForced) {
      navigateToMyFiles();
    }
  }, [isUpdatePasswordForced]);

  useEffectOnUpdate(() => {
    checkValidations();
  }, [newPassword, confirmedPassword]);

  const checkValidations = () => {
    const newValidations = {
      newPassword: '',
      confirmedPassword: '',
    };

    if (!newPassword || newPassword === '') {
      newValidations.newPassword = intl.formatMessage({ id: 'ERROR_REQUIRED_INPUT' });
    }
    if (!confirmedPassword || confirmedPassword === '') {
      newValidations.confirmedPassword = intl.formatMessage({ id: 'ERROR_REQUIRED_INPUT' });
    }
    if (newPassword && confirmedPassword && newPassword !== confirmedPassword) {
      newValidations.confirmedPassword = intl.formatMessage({
        id: 'ERROR_PASSWORDS_DO_NOT_MATCH',
      });
    }

    setValidations(newValidations);
  };

  const rules = (currentUser && currentUser.other.password_validators) || [];

  const [isOpen, setIsOpen] = useState(false);

  const handleInputChange = (value: string, name: 'confirmedPassword' | 'newPassword') => {
    switch (name) {
      case 'newPassword':
        setNewPassword(value);
        break;
      case 'confirmedPassword':
        setConfirmedPassword(value);
        break;
    }
  };

  const handleUpdatePassword = () => {
    updatePassword({ new: newPassword });
  };

  const handleSignedOut = () => {
    dispatch(signedOut());
  };

  return (
    <SecondaryAuthPageContainer title="UPDATE_PASSWORD" description="UPDATE_PASSWORD_DESCRIPTION">
      <InputField
        size="l"
        label={intl.formatMessage({ id: 'NEW_PASSWORD' })}
        feedbackContent={validations.newPassword}
        style={{ textAlign: 'left' }}
        testId="new-password"
      >
        <Popover.Root
          placement="left-start"
          open={isOpen}
          onOpenChange={() => {
            if (rules.length !== 0) setIsOpen(!isOpen);
          }}
          options={{
            offset: {
              crossAxis: 5,
              mainAxis: 30,
            },
          }}
          modal={true}
        >
          <Popover.Trigger>
            <PasswordInput
              name="newPassword"
              hasError={!!validations.newPassword}
              value={newPassword}
              onChange={(e) => handleInputChange(e.target.value, 'newPassword')}
              autoComplete="new-password"
              placeholder={intl.formatMessage({ id: 'NEW_PASSWORD_PLACEHOLDER' })}
              size="l"
              onBlur={() => {
                checkValidations();
                setIsOpen(false);
              }}
              onFocus={() => {
                if (rules.length !== 0) setIsOpen(true);
              }}
              testId="new-password"
            />
          </Popover.Trigger>
          <Popover.Content testId="password-checker">
            <PasswordCheck rules={rules} password={newPassword} setDisabled={setDisabled} />
          </Popover.Content>
        </Popover.Root>
      </InputField>
      <InputField
        size="l"
        label={intl.formatMessage({ id: 'CONFIRM_PASSWORD' })}
        feedbackContent={validations.confirmedPassword}
        style={{ textAlign: 'left' }}
        testId="confirm-password"
      >
        <PasswordInput
          name="confirmedPassword"
          size="l"
          value={confirmedPassword}
          onChange={(e) => handleInputChange(e.target.value, 'confirmedPassword')}
          placeholder={intl.formatMessage({ id: 'CONFIRM_PASSWORD_PLACEHOLDER' })}
          hasError={!!validations.confirmedPassword}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleUpdatePassword();
            }
          }}
          testId="confirm-password"
        />
      </InputField>
      <Button
        variant="primary"
        fullWidth
        size="l"
        onClick={handleUpdatePassword}
        disabled={disabled || !newPassword || newPassword !== confirmedPassword}
        testId="update-password"
        style={{ margin: '1rem 0 2rem' }}
      >
        <FormattedMessage id="CHANGE_PASSWORD" />
      </Button>
      <Button size="m" variant="link" onClick={handleSignedOut} testId="sign-in-to-dodoc">
        <FormattedMessage id="SIGN_IN_TO_DODOC" />
      </Button>
    </SecondaryAuthPageContainer>
  );
};

export default UpdatePasswordPage;
